import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, LogOut, RefreshCw, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "quoted", "won", "closed"];
const statusColor = {
  new: "bg-[#EAF0F6] text-[#1C2A39]", contacted: "bg-amber-100 text-amber-800",
  quoted: "bg-blue-100 text-blue-800", won: "bg-[#E8F1EB] text-[#0D9488]", closed: "bg-slate-100 text-slate-500",
};

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem("briteline_admin") || "");
  const [authed, setAuthed] = useState(!!localStorage.getItem("briteline_admin"));
  const [input, setInput] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (k) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/quotes`, { headers: { "X-Admin-Key": k } });
      setQuotes(res.data);
    } catch { toast.error("Unauthorized or failed to load."); logout(); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (authed && key) load(key); }, [authed, key, load]);

  const login = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { key: input });
      localStorage.setItem("briteline_admin", input);
      setKey(input); setAuthed(true);
      toast.success("Welcome back!");
    } catch { toast.error("Invalid admin key."); }
  };

  const logout = () => { localStorage.removeItem("briteline_admin"); setAuthed(false); setKey(""); setQuotes([]); };

  const setStatus = async (id, status) => {
    setQuotes((p) => p.map((q) => (q.id === id ? { ...q, status } : q)));
    try { await axios.patch(`${API}/admin/quotes/${id}`, { status }, { headers: { "X-Admin-Key": key } }); }
    catch { toast.error("Failed to update."); }
  };

  if (!authed) {
    return (
      <section className="pt-40 pb-24 min-h-screen bg-[#FAFAF9]">
        <div className="max-w-md mx-auto px-5">
          <h1 className="font-display font-extrabold text-4xl text-[#1C2A39] mb-2">Admin</h1>
          <p className="text-[#475569] mb-8">Enter your admin key to view quote requests.</p>
          <form onSubmit={login} className="bg-white rounded-3xl border border-slate-200 p-8">
            <Label className="text-sm font-medium text-[#1C2A39]">Admin key</Label>
            <Input data-testid="admin-key-input" type="password" value={input} onChange={(e) => setInput(e.target.value)} className="bg-[#FAFAF9] h-12 rounded-xl border-slate-200 mt-2" placeholder="••••••••" />
            <button data-testid="admin-login-btn" className="btn-primary w-full justify-center mt-6 !py-4">Sign in</button>
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-32 pb-24 min-h-screen bg-[#FAFAF9]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="font-display font-extrabold text-4xl text-[#1C2A39]">Quote Requests</h1>
            <p className="text-[#475569] mt-1">{quotes.length} total</p>
          </div>
          <div className="flex gap-3">
            <button data-testid="admin-refresh" onClick={() => load(key)} className="btn-ghost !py-2.5 !px-4"><RefreshCw size={16} /> Refresh</button>
            <button data-testid="admin-logout" onClick={logout} className="btn-ghost !py-2.5 !px-4"><LogOut size={16} /> Logout</button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#0D9488]" size={32} /></div>
        ) : quotes.length === 0 ? (
          <div className="text-center py-20 text-[#475569]"><Inbox size={40} className="mx-auto mb-4 text-slate-300" />No quote requests yet.</div>
        ) : (
          <div className="space-y-4" data-testid="admin-quotes-list">
            {quotes.map((q) => (
              <div key={q.id} className="bg-white rounded-2xl border border-slate-200 p-6 grid md:grid-cols-12 gap-4 items-start">
                <div className="md:col-span-3">
                  <p className="font-display font-medium text-lg text-[#1C2A39]">{q.name}</p>
                  <p className="text-sm text-[#475569]">{q.suburb}</p>
                  <p className="text-xs text-slate-400 mt-1">{new Date(q.created_at).toLocaleString()}</p>
                </div>
                <div className="md:col-span-3 text-sm">
                  <p className="text-[#1C2A39]">{q.email}</p>
                  <p className="text-[#475569]">{q.phone}</p>
                </div>
                <div className="md:col-span-4 text-sm">
                  <p className="text-[#0D9488] font-medium">{q.service_type}</p>
                  {q.property_size && <p className="text-[#475569]">{q.property_size}</p>}
                  {q.preferred_date && <p className="text-[#475569]">Preferred: {q.preferred_date}</p>}
                  {q.message && <p className="text-[#475569] mt-1 italic">"{q.message}"</p>}
                </div>
                <div className="md:col-span-2">
                  <span className={`inline-block text-xs px-3 py-1 rounded-full mb-2 ${statusColor[q.status] || statusColor.new}`}>{q.status}</span>
                  <Select value={q.status} onValueChange={(v) => setStatus(q.id, v)}>
                    <SelectTrigger data-testid={`status-${q.id}`} className="h-10 rounded-xl border-slate-200 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
